import { useMemo } from 'react'

import { useServerRequests } from '~/hooks/useRequests'
import { EvmAddress } from '~/utils/address'
import { createSingletonBatcher } from '~/utils/requests'

import type { NeynarUser } from '~/types'

type ResultByAddress = Record<string, NeynarUser[] | undefined>
type FarcasterResolved = NeynarUser & { pfp?: string }
type PostFn = ReturnType<typeof useServerRequests>['serverPostRequest']

// Batches addresses for 100ms and makes one request to /api/farcaster/lookup-by-address.
const getBatchFarcasterLookup = (serverPostRequest: PostFn) => {
  return createSingletonBatcher<
    string,
    string,
    NeynarUser[] | undefined,
    FarcasterResolved[]
  >('farcaster', {
    windowMs: 100,
    toKey: (address) => EvmAddress.format(address),
    request: async (addresses) => {
      const result = await serverPostRequest<{ results: ResultByAddress }>({
        path: '/api/farcaster/lookup-by-address',
        params: { addresses },
        // This is actually a fetch, it uses a POST to support large list of addresses, so we don't need an error message
        errorMessage: '',
      })
      return result?.data?.results ?? {}
    },
    select: (users) => {
      return users?.map((u) => ({ ...u, pfp: u.pfp_url }))
    },
  })
}

// Hook that provides a memoized batch lookup function
export const useBatchFarcasterLookup = () => {
  const { serverPostRequest } = useServerRequests()

  return useMemo(
    () => getBatchFarcasterLookup(serverPostRequest),
    [serverPostRequest],
  )
}
